import { useEffect, useState } from "react";
import { LifeBuoy, Mail, Phone, ExternalLink, CheckCircle2, Clock, AlertCircle, Send } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const STATUS_META = {
  open:        { label: "OPEN",        Icon: AlertCircle,    bg: "bg-[#2E9F3A]",  text: "text-white",  ring: "border-[#2E9F3A]" },
  in_progress: { label: "IN PROGRESS", Icon: Clock,          bg: "bg-[#F39A1F]",  text: "text-[#0F172A]", ring: "border-[#F39A1F]" },
  resolved:    { label: "RESOLVED",    Icon: CheckCircle2,   bg: "bg-[#3B7A3B]",  text: "text-white",  ring: "border-[#3B7A3B]" },
};

function formatDate(s) {
  if (!s) return "";
  try { return new Date(s).toLocaleString(); } catch { return s; }
}

/**
 * HelpdeskTab — Mike's order sheet. Visible only to admins.
 * Members see only their own tickets here (becomes their "My Tickets" view).
 */
export default function HelpdeskTab() {
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({open:0,in_progress:0,resolved:0,total:0});
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.is_admin;

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/help/tickets");
      setTickets(r.data.tickets || []);
      setCounts(r.data.counts || {});
    } catch { /* noop */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const visible = filter === "all" ? tickets : tickets.filter(t => t.status === filter);

  const sendReply = async () => {
    if (!reply.trim()) { toast.error("Type a reply first."); return; }
    setSending(true);
    try {
      await api.post(`/help/tickets/${active.id}/reply`, { body: reply.trim() });
      toast.success("Reply sent — member emailed.");
      setReply("");
      await load();
      const fresh = (await api.get("/help/tickets")).data.tickets.find(t => t.id === active.id);
      if (fresh) setActive(fresh);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send reply.");
    }
    setSending(false);
  };

  const setStatus = async (status) => {
    try {
      await api.post(`/help/tickets/${active.id}/status`, { status });
      toast.success("Status updated.");
      await load();
      setActive(a => ({ ...a, status }));
    } catch {
      toast.error("Could not update status.");
    }
  };

  return (
    <div data-testid="helpdesk-tab" className="space-y-6">
      {/* Header */}
      <section className="rounded-2xl bg-gradient-to-br from-[#0F172A] via-[#2A2521] to-[#0F172A] text-white p-6 md:p-8 shadow-2xl border-2 border-[#F39A1F]/40">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="w-14 h-14 rounded-full bg-[#2E9F3A] flex items-center justify-center shrink-0">
            <LifeBuoy className="w-7 h-7 text-white"/>
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="text-[10px] tracking-wider-cap font-bold text-[#F39A1F]">★ {isAdmin ? "MIKE'S ORDER SHEET" : "YOUR HELP TICKETS"}</div>
            <h2 className="font-serif-display text-3xl md:text-4xl uppercase">Helpdesk</h2>
            <p className="text-white/80 text-sm mt-1">{isAdmin ? "Every member's question, all in one place. Reply by email with one click." : "All your help requests. Mike or his team replies by email — usually within 24 hours."}</p>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="px-4 py-2 rounded-md bg-white/5"><div className="text-2xl text-[#F39A1F]">{counts.open || 0}</div><div className="text-[9px] tracking-wider-cap text-white/60">Open</div></div>
            <div className="px-4 py-2 rounded-md bg-white/5"><div className="text-2xl text-[#F39A1F]">{counts.in_progress || 0}</div><div className="text-[9px] tracking-wider-cap text-white/60">In Progress</div></div>
            <div className="px-4 py-2 rounded-md bg-white/5"><div className="text-2xl text-[#F39A1F]">{counts.resolved || 0}</div><div className="text-[9px] tracking-wider-cap text-white/60">Resolved</div></div>
          </div>
        </div>
      </section>

      {/* Filter pills */}
      <div className="inline-flex flex-wrap gap-1 rounded-full bg-white p-1 border border-border">
        {[["all","All"],["open","Open"],["in_progress","In Progress"],["resolved","Resolved"]].map(([k,l]) => (
          <button key={k} data-testid={`helpdesk-filter-${k}`} onClick={() => setFilter(k)} className={`px-4 py-1.5 rounded-full text-xs font-overline uppercase tracking-wider-cap transition ${filter===k?"bg-red-wine text-white shadow":"text-foreground hover:bg-white"}`}>{l}</button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-12 text-foreground/60">Loading tickets…</div>
      ) : visible.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-[#F39A1F]/40 bg-white/40 p-10 text-center">
          <LifeBuoy className="w-12 h-12 text-[#F39A1F] mx-auto mb-3"/>
          <div className="font-serif-display text-2xl text-red-wine">No tickets {filter !== "all" ? `with status "${filter}"` : "yet"}.</div>
          <div className="text-foreground/80 text-sm mt-1">{isAdmin ? "When members hit 'Help Me, Mike!' their messages land here." : "Hit the red 'Help Me, Mike!' button anywhere on the dashboard."}</div>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_360px] gap-5">
          {/* Ticket list */}
          <div className="space-y-3" data-testid="helpdesk-tickets-list">
            {visible.map(t => {
              const meta = STATUS_META[t.status] || STATUS_META.open;
              const lastReply = (t.messages || []).slice(-1)[0];
              return (
                <button
                  key={t.id}
                  onClick={() => { setActive(t); setReply(""); }}
                  data-testid={`helpdesk-ticket-${t.id}`}
                  type="button"
                  className={`w-full text-left rounded-xl border-2 ${active?.id === t.id ? "border-[#2E9F3A] shadow-lg" : meta.ring} bg-white p-4 hover:shadow-md transition`}
                >
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className={`px-2.5 py-1 rounded-full text-[10px] tracking-wider-cap font-bold uppercase inline-flex items-center gap-1 ${meta.bg} ${meta.text}`}>
                      <meta.Icon className="w-3 h-3"/>{meta.label}
                    </div>
                    <div className="text-[10px] tracking-wider-cap font-bold text-foreground/60">{t.short_id}</div>
                    <div className="text-[10px] tracking-wider-cap font-bold text-foreground/60 ml-auto">{formatDate(t.updated_at)}</div>
                  </div>
                  <div className="mt-2 font-bold text-red-wine text-base leading-tight">{t.subject || "(no subject)"}</div>
                  {isAdmin && (
                    <div className="text-[11px] text-foreground/85 mt-0.5"><strong>{t.user_name}</strong> · {t.user_email}{t.phone && <> · {t.phone}</>}</div>
                  )}
                  <div className="text-sm text-foreground mt-1.5 line-clamp-2">{t.body}</div>
                  {(t.messages || []).length > 0 && (
                    <div className="mt-2 text-[10px] tracking-wider-cap font-bold text-[#3B7A3B]">↳ {(t.messages||[]).length} {(t.messages||[]).length===1?"reply":"replies"} · last from {lastReply.from}</div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Reply panel */}
          {active && (
            <div className="rounded-xl border-2 border-[#F39A1F]/40 bg-white p-4 lg:sticky lg:top-6 self-start" data-testid="helpdesk-reply-panel">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] tracking-wider-cap font-bold text-gold-deep">{active.short_id}</div>
                <button onClick={() => setActive(null)} className="text-foreground/60 hover:text-red-wine text-xs uppercase tracking-wider-cap font-bold">Close</button>
              </div>
              <div className="font-bold text-red-wine text-lg leading-tight mb-1">{active.subject || "(no subject)"}</div>
              <div className="text-[11px] text-foreground/85 mb-3"><Mail className="w-3 h-3 inline mr-1"/>{active.user_email}{active.phone && <> · <Phone className="w-3 h-3 inline mx-1"/>{active.phone}</>}</div>
              {active.page_url && (
                <a href={active.page_url} target="_blank" rel="noreferrer" className="text-[11px] text-[#D7800A] hover:text-red-wine underline inline-flex items-center gap-1 mb-2"><ExternalLink className="w-3 h-3"/>{active.page_url}</a>
              )}
              <div className="bg-white/60 rounded-md p-3 text-sm text-foreground whitespace-pre-wrap mb-3">{active.body}</div>
              {active.screenshot_url && (
                <a href={active.screenshot_url} target="_blank" rel="noreferrer" className="block mb-3"><img src={active.screenshot_url} alt="" className="rounded-md max-h-40 object-cover border-2 border-foreground/10"/></a>
              )}

              {/* Replies thread */}
              {(active.messages || []).map((m, i) => (
                <div key={i} className="bg-white border-l-4 border-[#F39A1F] rounded p-3 text-sm text-foreground mb-2">
                  <div className="text-[10px] tracking-wider-cap font-bold text-gold-deep mb-1">{m.from} · {formatDate(m.at)}</div>
                  <div className="whitespace-pre-wrap">{m.body}</div>
                </div>
              ))}

              {/* Admin actions */}
              {isAdmin ? (
                <>
                  <textarea
                    rows={4} value={reply} onChange={e=>setReply(e.target.value)}
                    placeholder="Type your reply — sends as an email to the member."
                    data-testid="helpdesk-reply-input"
                    className="w-full mt-2 border-2 border-foreground/20 focus:border-[#F39A1F] rounded-md px-3 py-2 text-sm outline-none"
                  />
                  <button
                    onClick={sendReply} disabled={sending}
                    data-testid="helpdesk-reply-send"
                    className="w-full mt-2 bg-[#2E9F3A] hover:bg-[#1F8A2A] disabled:opacity-60 text-white px-4 py-2.5 rounded-md text-xs tracking-wider-cap font-bold uppercase inline-flex items-center justify-center gap-2 transition"
                  >
                    <Send className="w-3.5 h-3.5"/>{sending ? "Sending…" : "Send Reply (emails member)"}
                  </button>

                  <div className="mt-3 grid grid-cols-3 gap-1.5">
                    {["open","in_progress","resolved"].map(s => (
                      <button
                        key={s} onClick={() => setStatus(s)}
                        data-testid={`helpdesk-status-${s}`}
                        className={`text-[10px] tracking-wider-cap font-bold uppercase py-1.5 rounded-md border-2 transition ${active.status === s ? `${STATUS_META[s].bg} ${STATUS_META[s].text} ${STATUS_META[s].ring}` : "bg-white text-foreground border-foreground/15 hover:border-[#F39A1F]"}`}
                      >{STATUS_META[s].label}</button>
                    ))}
                  </div>
                </>
              ) : (
                <div className="mt-3 text-xs text-foreground/70 italic">Mike (or his team) will reply by email. You'll see their reply land in your inbox first; the thread also shows here.</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
